
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TestResultDisplay } from "./TestResultDisplay";
import { supabase } from "@/integrations/supabase/client";

const youtubePostTestSchema = z.object({
  videoLink: z.string().min(1, { message: "YouTube video link is required" })
    .url({ message: "Please enter a valid URL" })
    .refine(val => val.includes("youtube.com/") || val.includes("youtu.be/"), {
      message: "Must be a valid YouTube video link"
    }),
});

interface YoutubeSettingsProps {
  form: UseFormReturn<{
    youtubeApiKey?: string;
    [key: string]: string | undefined;
  }>;
}

export const YoutubeSettings = ({ form }: YoutubeSettingsProps) => {
  const { toast } = useToast();
  const [testingYoutube, setTestingYoutube] = useState(false);
  const [testingYoutubeLink, setTestingYoutubeLink] = useState(false);
  const [youtubeStatus, setYoutubeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [youtubeTestResult, setYoutubeTestResult] = useState<any>(null);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [showRawData, setShowRawData] = useState(false);

  const youtubeForm = useForm<z.infer<typeof youtubePostTestSchema>>({
    resolver: zodResolver(youtubePostTestSchema),
    defaultValues: {
      videoLink: "",
    },
  });

  const testYoutubeApi = async () => {
    const apiKey = form.getValues("youtubeApiKey");
    
    if (!apiKey) {
      toast({
        title: "API Key Required",
        description: "Please enter a YouTube API key before testing.",
        variant: "destructive",
      });
      return;
    }
    
    setTestingYoutube(true);
    
    try {
      const currentTimeStr = new Date().toISOString();
      
      // Save YouTube API Key
      const { data: existingYoutubeApiKey } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'youtube_api_key')
        .maybeSingle();
      
      if (existingYoutubeApiKey) {
        await supabase
          .from('integration_settings')
          .update({
            api_key: apiKey,
            updated_at: currentTimeStr
          })
          .eq('integration_name', 'youtube_api_key');
      } else {
        await supabase
          .from('integration_settings')
          .insert({
            integration_name: 'youtube_api_key',
            api_key: apiKey,
            updated_at: currentTimeStr
          });
      }
      
      // Also sync the API key to the edge function environment
      await supabase.functions.invoke('sync-youtube-token', {
        body: {}
      });
      
      setYoutubeStatus('success');
      toast({
        title: "YouTube API Settings Saved",
        description: "Your YouTube API key has been saved and synced.",
      });
    } catch (error) {
      console.error("Error saving YouTube settings:", error);
      setYoutubeStatus('error');
      toast({
        title: "YouTube API Test Failed",
        description: "Unable to save YouTube API key.",
        variant: "destructive",
      });
    } finally {
      setTestingYoutube(false);
    }
  };

  const testYoutubeLink = async (data: z.infer<typeof youtubePostTestSchema>) => {
    setTestingYoutubeLink(true);
    setYoutubeTestResult(null);
    setYoutubeError(null);
    
    try {
      // First sync the YouTube API key
      toast({
        title: "Syncing YouTube Credentials",
        description: "Syncing your YouTube API key to the edge function...",
      });
      
      await supabase.functions.invoke('sync-youtube-token', {
        body: {}
      });
      
      // Then test the YouTube video link
      toast({
        title: "Testing YouTube Link",
        description: "Fetching metrics for the provided URL...",
      });
      
      const response = await supabase.functions.invoke('youtube-track-metrics', {
        body: {
          videoLink: data.videoLink
        },
      });
      
      const result = response.data;
      
      if (response.error || result?.error) {
        setYoutubeError(result?.details || result?.error || response.error?.message || "Unknown error occurred");
        toast({
          title: "YouTube API Error",
          description: result?.details || result?.error || response.error?.message || "Unknown error occurred",
          variant: "destructive",
        });
      } else {
        setYoutubeTestResult(result);
        
        toast({
          title: "YouTube Video Link Processed",
          description: result?.message || "Processed video metrics successfully.",
          variant: "default",
        });
      }
    } catch (error: any) {
      setYoutubeError(error.message || "An error occurred while processing the YouTube video link.");
      toast({
        title: "Error Processing Link",
        description: error.message || "An error occurred while processing the YouTube video link.",
        variant: "destructive",
      });
    } finally {
      setTestingYoutubeLink(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>YouTube API</CardTitle>
        <CardDescription>Configure your YouTube API integration for tracking video metrics.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <FormField
            control={form.control}
            name="youtubeApiKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>YouTube API Key</FormLabel>
                <FormControl>
                  <Input {...field} type="password" placeholder="Enter your YouTube API Key" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Form>
      </CardContent>
      <CardFooter className="flex flex-col items-start space-y-4">
        <Button 
          variant="default" 
          onClick={testYoutubeApi} 
          disabled={testingYoutube}
        >
          {testingYoutube ? "Testing..." : "Save YouTube Settings"}
          {youtubeStatus === 'success' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
          {youtubeStatus === 'error' && <AlertCircle className="ml-2 h-4 w-4 text-red-500" />}
        </Button>
        
        <div className="w-full">
          <h3 className="text-sm font-medium mb-2">Test YouTube Video Link</h3>
          
          <div className="border border-border rounded-md p-4">
            <Form {...youtubeForm}>
              <form onSubmit={youtubeForm.handleSubmit(testYoutubeLink)} className="space-y-4">
                <FormField
                  control={youtubeForm.control}
                  name="videoLink"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Video Link</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., https://www.youtube.com/watch?v=abc123" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button type="submit" disabled={testingYoutubeLink}>
                  {testingYoutubeLink ? "Processing..." : "Test Link"}
                </Button>
              </form>
            </Form>
          </div>
          
          <TestResultDisplay
            result={youtubeTestResult}
            error={youtubeError}
            showRawData={showRawData}
            onToggleRawData={() => setShowRawData(!showRawData)}
            title="YouTube API Response"
          />
        </div>
      </CardFooter>
    </Card>
  );
};
